import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { walletService } from '../services/wallet';

const WalletConnect = ({ onAddressReceived }) => {
  const [walletStatus, setWalletStatus] = useState({
    isInstalled: false,
    isConnected: false,
    address: '',
    loading: false,
    error: ''
  });
  
  // 使用 useRef 来存储上一次的地址，避免不必要的回调
  const [prevAddress, setPrevAddress] = useState('');
  
  // 检查钱包状态的函数
  const checkWalletStatus = useCallback(() => {
    // 检查钱包安装状态
    const isInstalled = walletService.isWalletInstalled();
    
    // 检查钱包连接状态
    const { connected, address } = walletService.getConnectionStatus();
    
    setWalletStatus({
      isInstalled,
      isConnected: connected,
      address: address || '',
      loading: false,
      error: ''
    });
    
    // 只有当地址变化时，才通知父组件
    if (connected && address && address !== prevAddress) {
      setPrevAddress(address);
      onAddressReceived(address);
    } else if (!connected && prevAddress) {
      setPrevAddress('');
      onAddressReceived('');
    }
  }, [onAddressReceived, prevAddress]);
  
  // 组件挂载和 checkWalletStatus 变化时执行
  useEffect(() => {
    checkWalletStatus();
    
    // 可选：添加事件监听器处理钱包状态变化
    const handleAccountsChanged = () => {
      checkWalletStatus();
    };
    
    // 如果钱包提供了事件监听API
    if (window.okxwallet && window.okxwallet.on) {
      window.okxwallet.on('accountsChanged', handleAccountsChanged);
    }
    
    return () => {
      // 清理事件监听
      if (window.okxwallet && window.okxwallet.removeListener) {
        window.okxwallet.removeListener('accountsChanged', handleAccountsChanged);
      }
    };
  }, [checkWalletStatus]);
  
  const handleConnectWallet = async () => {
    try {
      setWalletStatus(prev => ({ ...prev, loading: true, error: '' }));
      
      const { connected, address } = await walletService.connectWallet();
      
      setWalletStatus({
        isInstalled: true,
        isConnected: connected,
        address: address || '',
        loading: false,
        error: ''
      });
      
      // 通知父组件已获取地址
      if (connected && address && address !== prevAddress) {
        setPrevAddress(address);
        onAddressReceived(address);
      }
    } catch (error) {
      setWalletStatus(prev => ({
        ...prev,
        loading: false,
        error: error.message || '连接钱包失败'
      }));
    }
  };
  
  const handleDisconnectWallet = () => {
    walletService.disconnectWallet();
    setWalletStatus({
      isInstalled: walletService.isWalletInstalled(),
      isConnected: false,
      address: '',
      loading: false,
      error: ''
    });
    
    // 通知父组件已断开连接
    if (prevAddress) {
      setPrevAddress('');
      onAddressReceived('');
    }
  };
  
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 8)}...${address.substring(address.length - 6)}`;
  };
  
  return (
    <WalletContainer>
      {!walletStatus.isInstalled ? (
        <WalletMessage>
          OKX 钱包未安装，请先
          <WalletLink 
            href="https://www.okx.com/web3" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            安装 OKX 钱包
          </WalletLink>
        </WalletMessage>
      ) : walletStatus.isConnected ? (
        <ConnectedContainer>
          <AddressDisplay>
            <AddressLabel>已连接：</AddressLabel>
            <AddressText title={walletStatus.address}>
              {formatAddress(walletStatus.address)}
            </AddressText>
          </AddressDisplay>
          <DisconnectButton onClick={handleDisconnectWallet}>
            断开连接
          </DisconnectButton>
        </ConnectedContainer>
      ) : (
        <ConnectButton 
          onClick={handleConnectWallet}
          disabled={walletStatus.loading}
        >
          {walletStatus.loading ? '连接中...' : '连接 OKX 钱包'}
        </ConnectButton>
      )}
      
      {walletStatus.error && (
        <ErrorMessage>{walletStatus.error}</ErrorMessage>
      )}
    </WalletContainer>
  );
};
// 样式组件
const WalletContainer = styled.div`
  margin-bottom: 30px;
`;

const WalletMessage = styled.div`
  background-color: rgba(40, 40, 40, 0.8);
  color: ${({ theme }) => theme.colors.text};
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 10px;
`;

const WalletLink = styled.a`
  color: ${({ theme }) => theme.colors.secondary};
  margin-left: 5px;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ConnectButton = styled.button`
  width: 100%;
  padding: 14px;
  background-color: #5b5b5b;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s, transform 0.2s;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background-color: #4a4a4a;
    transform: translateY(-2px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ConnectedContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: rgba(40, 40, 40, 0.8);
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 10px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const AddressDisplay = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

const AddressLabel = styled.span`
  color: ${({ theme }) => theme.colors.text};
  margin-right: 5px;
`;

const AddressText = styled.span`
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 500;
  cursor: pointer;
`;

const DisconnectButton = styled.button`
  background-color: transparent;
  color: #ff5555;
  border: 1px solid #ff5555;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(255, 85, 85, 0.1);
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.errorColor};
  background-color: rgba(255, 85, 85, 0.1);
  padding: 10px 15px;
  border-radius: 6px;
  margin-top: 10px;
  font-size: 14px;
`;

export default WalletConnect;